export const careStatusSchema = {
  title: "Care Status",
  type: "object",
  required: ["careStatusSection0", "primaryCarePhysician", "additionalInformation1", "signatureInfo"],
  properties: {
    careStatusSection0: {
      type: "object",
      title: "Care Status",
      description: "Tell us about the client's current care situation",
      properties: {
        hospiceStatus: {
          type: "boolean",
          title: "Hospice Status",
          description: "Is the client currently in Hospice?",
        },
        homeHealthStatus: {
          type: "boolean",
          title: "Home Health Status",
          description: "Is the client currently in Home Health?",
        }
      },
      dependencies: {
        hospiceStatus: {
          oneOf: [
            {
              properties: {
                hospiceStatus: { const: true },
                hospiceAgency: {
                  type: "string",
                  title: "Hospice Agency",
                  minLength: 2,
                  maxLength: 25,
                  errorMessage: {
                    required: "Hospice agency is required",
                    minLength: "Hospice agency must be at least 2 characters",
                    maxLength: "Hospice agency must be under 25 characters",
                  },
                },
                hospiceRepName: {
                  type: "string",
                  title: "Representative Name",
                  minLength: 2,
                  maxLength: 25,
                  errorMessage: {
                    required: "Representative name is required",
                    minLength: "Representative name must be at least 2 characters",
                    maxLength: "Representative name must be under 25 characters",
                  },
                },
                hospiceRepPhone: {
                  type: "string",
                  title: "Representative Phone",
                  pattern: "^\\(\\d{3}\\) \\d{3}-\\d{4}$",
                  errorMessage: {
                    required: "Representative phone is required",
                    pattern: "Enter a valid phone number in format (xxx) xxx-xxxx",
                  },
                },
              },
              required: ["hospiceAgency", "hospiceRepName", "hospiceRepPhone"],
            },
            {
              properties: {
                hospiceStatus: { const: false },
              },
            },
          ],
        },
        homeHealthStatus: {
          oneOf: [
            {
              properties: {
                homeHealthStatus: { const: true },
                homeAgency: {
                  type: "string",
                  title: "Home Agency",
                  minLength: 2,
                  maxLength: 25,
                  errorMessage: {
                    required: "Home health agency is required",
                    minLength: "Home health agency must be at least 2 characters",
                    maxLength: "Home health agency must be under 25 characters",
                  },
                },
                homeRepName: {
                  type: "string",
                  title: "Home Representative Name",
                  minLength: 2,
                  maxLength: 25,
                  errorMessage: {
                    required: "Representative name is required",
                    minLength: "Representative name must be at least 2 characters",
                    maxLength: "Representative name must be under 25 characters",
                  },
                },
                homeRepPhone: {
                  type: "string",
                  title: "Home Representative Phone",
                  pattern: "^\\(\\d{3}\\) \\d{3}-\\d{4}$",
                  errorMessage: {
                    required: "Representative phone is required",
                    pattern: "Enter a valid phone number in format (xxx) xxx-xxxx",
                  },
                },
              },
              required: ["homeAgency", "homeRepName", "homeRepPhone"],
            },
            {
              properties: {
                homeHealthStatus: { const: false },
              },
            },
          ],
        },
      },
    },

    primaryCarePhysician: {
      type: "object",
      title: "Primary Care Physician",
      description: "Provide information about the client's primary doctor",
      required: ["physicianFirstName", "physicianLastName", "physicianPhoneNumber"],
      properties: {
        physicianFirstName: {
          type: "string",
          title: "First Name",
          minLength: 1,
          maxLength: 25,
          errorMessage: {
            required: "First name is required",
            minLength: "First name cannot be empty",
            maxLength: "First name must be under 25 characters",
          },
        },
        physicianLastName: {
          type: "string",
          title: "Last Name",
          minLength: 1,
          maxLength: 25,
          errorMessage: {
            required: "Last name is required",
            minLength: "Last name cannot be empty",
            maxLength: "Last name must be under 25 characters",

          },
        },
        physicianPhoneNumber: {
          type: "string",
          title: "Phone Number",
          pattern: "^\\(\\d{3}\\) \\d{3}-\\d{4}$",
          errorMessage: {
            required: "Physician phone number is required",
            pattern: "Enter a valid phone number in format (xxx) xxx-xxxx",
          },
        },
      },
    },

    additionalInformation1: {
      type: "object",
      title: "Additional Information",
      description: "Share any other relevant details about the client's care needs",
      properties: {
        additionalInfo: {
          type: "string",
          title: "Additional Details",
          maxLength: 500,
          errorMessage: {
            maxLength: "Additional details must be under 500 characters"
          }
        },
      },
    },
    signatureInfo: {
      type: "object",
      title: "Individualized Service Plan Signature",
      required: ["signature", "NameOfSigner"],
      properties: {
        signature: {
          type: "string",
          title: "Please sign below stating that you agree to the required items above.",
          errorMessage: {
            required: "Signature is required",
          }
        },
        NameOfSigner: {
          type: "string",
          title: "Name of Signer",
          maxLength: 50,
          errorMessage: {
            required: "Name of signer is required",
            maxLength: "Name of signer must be under 50 characters",
          }
        },
      },
    }

  },
};

export const careStatusUiSchema = {
  careStatusSection0: {
    "ui:order": [
      "hospiceStatus",
      'hospiceAgency',
      "hospiceRepName",
      "hospiceRepPhone",
      "homeHealthStatus",
      'homeAgency',
      "homeRepName",
      "homeRepPhone",
    ],
    hospiceStatus: {
      "ui:widget": "CardCheckboxWidget", // or your custom MUI switch if you're using it
      "ui:md": 12,
      "ui:options": {
        label: false,
      }
    },
    hospiceAgency: { "ui:md": 4, "ui:condition": (formData) => formData?.careStatusSection0?.hospiceStatus === true },
    hospiceRepName: { "ui:md": 4, "ui:condition": (formData) => formData?.careStatusSection0?.hospiceStatus === true },
    hospiceRepPhone: {
      "ui:md": 4, "ui:widget": "PhoneWidget", "ui:options": {
        inputType: "tel",
        disabled: false,
        readonly: false,
        hideError: false,
      }, "ui:condition": (formData) => formData?.careStatusSection0?.hospiceStatus === true
    },

    homeHealthStatus: {
      "ui:widget": "CardCheckboxWidget",
      "ui:md": 12,
      "ui:options": {
        label: false,
      }
    },

    homeAgency: { "ui:md": 4, "ui:condition": (formData) => formData?.careStatusSection0?.homeHealthStatus === true },
    homeRepName: { "ui:md": 4, "ui:condition": (formData) => formData?.careStatusSection0?.homeHealthStatus === true },
    homeRepPhone: {
      "ui:md": 4, "ui:widget": "PhoneWidget", "ui:options": {
        inputType: "tel",
        disabled: false,
        readonly: false,
        hideError: false,
      }, "ui:condition": (formData) => formData?.careStatusSection0?.homeHealthStatus === true
    },
  },
  primaryCarePhysician: {
    physicianFirstName: { "ui:md": 6 },
    physicianLastName: { "ui:md": 6 },
    physicianPhoneNumber: {
      "ui:md": 6, "ui:widget": "PhoneWidget", "ui:options": {
        inputType: "tel",
        disabled: false,
        readonly: false,
        hideError: false,
      },
    },
  },
  additionalInformation1: {
    additionalInfo: { "ui:md": 12, "ui:widget": "TextareaAutosizeWidget",
			"ui:options": {
				rows: 2
			}, },
  },
  signatureInfo: {
    signature: {
      "ui:widget": "SignatureWidget", // or your custom widget name
      "ui:options": {
        label: false,
      },
      "ui:md": 12,
    },
    NameOfSigner: {
      "ui:md": 6,
    },
  },
};